import { courseService } from '@/services/course.service';
import { reviewService } from '@/services/review.service';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import {
  BookOpen,
  ChevronDown,
  Clock,
  Edit,
  Grid,
  List,
  Search,
  SlidersHorizontal,
  Sparkles,
  Star,
  Users
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Link, useNavigate } from 'react-router-dom';
import { CardSkeleton } from '@/components/ui/SkeletonLoader';
import Pagination from '@/components/ui/Pagination';
import SimpleSelect, { SelectOption } from '@/components/ui/SimpleSelect';
import usePermissionRouteAccess from '@/hooks/usePermissionRouteAccess';
import useHasPermission from '@/hooks/useHasPermission';
import { COURSE_CATALOG_PERMISSION, COURSE_GENERATION_PERMISSION } from '@/constants/constant-permissions';

interface Course {
  _id: string;
  id?: string;
  title: string;
  description: string;
  thumbnail?: string;
  thumbnail_image_url?: string;
  banner_image_url?: string;
  difficulty: string;
  rating: number;
  review_count?: number;
  enrolledUsers?: number;
  users_assigned?: number;
  duration?: number;
  module_count?: number;
  stage?: 'generating' | 'draft' | 'published' | 'unpublished';
  status?: 'generating' | 'draft' | 'published' | 'unpublished';
  estimated_duration?: {
    total: {
      hours: number;
      minutes: number;
    };
  };
  createdAt?: string;
  updatedAt?: string;
  source_type?: string; // 'scorm_imported' for SCORM courses (not editable)
  content_delivery_type?: string; // html | html_with_audio | video_presentation | avatar_video
}

// Course Type = how the course was created (backend `source_type`).
const COURSE_TYPE_OPTIONS = [
  { value: '', label: 'All Courses' },
  { value: 'ai', label: 'AI Course' },
  { value: 'manual', label: 'Manual Course' },
  { value: 'scorm_imported', label: 'SCORM Course' },
];

// Content Format = the course's delivery type (backend `content_delivery_type`).
const CONTENT_FORMAT_LABELS: Record<string, string> = {
  html: 'HTML',
  html_with_audio: 'HTML with Audio',
  video_presentation: 'Video Presentation',
  avatar_video: 'Avatar Video',
};

// Formats each course type can actually have, so the second dropdown never
// offers a combination that returns nothing. Manual creation excludes
// avatar_video (see ManualContentDeliveryType in the SDK) and SCORM packages
// carry no delivery type at all, so the format filter is hidden for them.
const FORMATS_BY_COURSE_TYPE: Record<string, string[]> = {
  '': ['html', 'html_with_audio', 'video_presentation', 'avatar_video'],
  ai: ['html', 'html_with_audio', 'video_presentation', 'avatar_video'],
  manual: ['html', 'html_with_audio', 'video_presentation'],
  scorm_imported: [],
};

const getAllowedFormats = (courseType: string) =>
  FORMATS_BY_COURSE_TYPE[courseType] ?? FORMATS_BY_COURSE_TYPE[''];

const CourseCard = ({
  course,
  isListView = false,
  onRatingClick,
  canWrite = true
}: {
  course: Course;
  isListView?: boolean;
  onRatingClick?: (courseId: string) => void;
  canWrite?: boolean;
}) => {
  const getStageColor = (stage?: string) => {
    switch (stage) {
      case 'published':
        return 'bg-green-100 text-green-700 border-green-300';
      case 'draft':
        return 'bg-yellow-100 text-yellow-700 border-yellow-300';
      case 'unpublished':
        return 'bg-gray-100 text-gray-700 border-gray-300';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-300';
    }
  };

  // Use stage or status field
  const courseStage = course.stage || course.status || 'draft';

  // Falls back to the BookOpen placeholder when the thumbnail image fails to load.
  const [imgError, setImgError] = useState(false);

  // Get thumbnail URL — courses without an image (e.g. SCORM imports) render a
  // blue BookOpen placeholder instead of a shared stock photo. Empty / 'null' /
  // 'undefined' values are treated as "no image".
  const rawThumbnail = (course.thumbnail_image_url || course.thumbnail || '').trim();
  const thumbnailUrl =
    rawThumbnail &&
    rawThumbnail.toLowerCase() !== 'null' &&
    rawThumbnail.toLowerCase() !== 'undefined'
      ? rawThumbnail
      : '';

  // Reset the error flag when the thumbnail changes, so a card that once failed to load
  // (e.g. a broken URL later fixed via edit) recovers without a full page reload.
  useEffect(() => {
    setImgError(false);
  }, [thumbnailUrl]);

  // Get users count (users_assigned or enrolledUsers)
  const usersCount = course.users_assigned ?? course.enrolledUsers ?? 0;

  // Calculate duration in minutes
  const getDurationInMinutes = () => {
    if (course.estimated_duration?.total) {
      const { hours, minutes } = course.estimated_duration.total;
      return (hours * 60) + minutes;
    }
    return course.duration || 0;
  };

  const formatDuration = () => {
    const totalMinutes = getDurationInMinutes();
    if (totalMinutes === 0) return '0m';
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    if (hours > 0) {
      return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
    }
    return `${minutes}m`;
  };

  return (
    <div className={`card card-hover ${isListView ? 'flex gap-4' : ''}`}>
      <div className={`relative ${isListView ? 'w-48 flex-shrink-0' : ''}`}>
        {thumbnailUrl && !imgError ? (
          <img
            src={thumbnailUrl}
            alt={course.title}
            onError={() => setImgError(true)}
            className={`w-full ${isListView ? 'h-32' : 'h-48'} object-cover rounded-lg`}
          />
        ) : (
          <div
            className={`w-full ${isListView ? 'h-32' : 'h-48'} rounded-lg bg-gradient-to-br from-brand-blue-50 to-brand-blue-100 flex items-center justify-center`}
          >
            <BookOpen className={`${isListView ? 'w-10 h-10' : 'w-14 h-14'} text-brand-blue-400`} />
          </div>
        )}
        <div className="absolute top-2 right-2">
          <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getStageColor(courseStage)} backdrop-blur-sm`}>
            {courseStage === 'published' ? '🟢' : courseStage === 'draft' ? '🟡' : '⚫'} {courseStage}
          </span>
        </div>
        <div className="absolute bottom-2 left-2">
          <span className={`px-2 py-1 text-xs font-medium rounded capitalize ${course.difficulty?.toLowerCase() === 'beginner' ? 'bg-green-100 text-green-700' :
              course.difficulty?.toLowerCase() === 'intermediate' ? 'bg-yellow-100 text-yellow-700' :
                'bg-red-100 text-red-700'
            }`}>
            {course.difficulty || 'N/A'}
          </span>
        </div>
      </div>

      <div className={`${isListView ? 'flex-1' : 'mt-4'}`}>
        <h3 className={`font-semibold text-gray-900 ${isListView ? 'text-lg' : 'text-base'} line-clamp-2`}>
          <Link
            to={canWrite ? `/admin/edit-course/${course.id || course._id}` : "#"}
            onClick={(e) => !canWrite && e.preventDefault()}
            title={!canWrite ? 'You do not have permission to perform this action' : undefined}
            className={`hover:text-primary-600 ${!canWrite ? 'cursor-not-allowed opacity-70 pointer-events-auto' : ''}`}
          >
            {course.title}
          </Link>
        </h3>
        <p className={`text-gray-600 text-sm mb-3 ${isListView ? 'line-clamp-2' : 'line-clamp-3'}`}>
          {course.description}
        </p>
        <div className="flex items-center flex-wrap gap-x-4 gap-y-1 text-sm text-gray-600 mb-4">
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (canWrite) onRatingClick?.(course.id || course._id);
            }}
            disabled={!canWrite}
            className={`flex items-center gap-1.5 hover:text-yellow-600 transition-colors ${!canWrite ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
            title={!canWrite ? 'You do not have permission to perform this action' : 'View reviews'}
          >
            <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
            <span className="font-medium">{course.rating?.toFixed(1) || '0.0'}</span>
            {course.review_count !== undefined && (
              <span className="text-gray-400">({course.review_count} {course.review_count === 1 ? 'rating' : 'ratings'})</span>
            )}
          </button>
          <div className="flex items-center gap-1">
            <Users className="w-4 h-4" />
            <span>{usersCount.toLocaleString()}</span>
          </div>
          {course.source_type !== 'scorm_imported' && (
            <div className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              <span>{formatDuration()}</span>
            </div>
          )}
          {course.module_count !== undefined && (
            <div className="flex items-center gap-1">
              <BookOpen className="w-4 h-4" />
              <span>{course.module_count} modules</span>
            </div>
          )}
        </div>
        <div className="flex items-center justify-between">
          {canWrite ? (
            <Link to={`/admin/edit-course/${course.id || course._id}`} className="btn btn-primary btn-sm inline-flex items-center gap-2">
              <Edit size={14} />
              Manage
            </Link>
          ) : (
            <button disabled className="btn btn-primary btn-sm inline-flex items-center gap-2 opacity-50 cursor-not-allowed">
              <Edit size={14} />
              Manage
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default function CourseCatalog() {
  // Permission guard — redirect if user lacks both read and write
  usePermissionRouteAccess([
    COURSE_CATALOG_PERMISSION.COURSE_CATALOG_READ,
    COURSE_CATALOG_PERMISSION.COURSE_CATALOG_WRITE,
  ]);

  // Check write permission for action buttons
  const canWrite = useHasPermission([COURSE_CATALOG_PERMISSION.COURSE_CATALOG_WRITE]);
  const canGenerate = useHasPermission([COURSE_GENERATION_PERMISSION.COURSE_GENERATION_WRITE]);

  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(21);
  const [filters, setFilters] = useState({
    search: '',
    difficulty: '',
    stage: '',
    courseType: '',    // '' = all; else 'ai' | 'manual' | 'scorm_imported'
    contentFormat: '', // '' = all; else a value allowed for the selected courseType
    sortBy: 'updatedAt' as 'createdAt' | 'updatedAt' | 'title' | 'rating' | 'users_assigned',
    sortOrder: 'desc' as 'asc' | 'desc',
  });

  const queryParams = useMemo(() => {
    const { courseType, contentFormat, ...rest } = filters;
    const params: any = {
      page,
      limit,
      ...rest,
    };

    // The two dropdowns map to independent backend params and combine freely.
    if (courseType) params.source_type = courseType;
    if (contentFormat) params.content_delivery_type = contentFormat;

    // Remove empty values
    Object.keys(params).forEach(key => {
      if (!params[key] || params[key] === 'all') {
        delete params[key];
      }
    });

    return params;
  }, [filters, page, limit]);

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ['minimal-courses', queryParams],
    queryFn: () => courseService.getMinimalCourses(queryParams),
    placeholderData: keepPreviousData,
  });

  // keepPreviousData keeps the previous page on screen while a filter/sort/search/
  // page change is in flight, so isLoading is only true on the very first load.
  // Skeletons are driven off isFetching so every refetch shows the loader.
  const isLoadingCourses = isLoading || isFetching;

  // Fetch review stats for all courses
  const courseIds = data?.courses?.map((c: Course) => c.id || c._id) || [];
  const { data: reviewStatsMap } = useQuery({
    queryKey: ['course-review-stats', courseIds],
    queryFn: async () => {
      if (courseIds.length === 0) return new Map();
      return await reviewService.getBatchCourseStats();
    },
    enabled: courseIds.length > 0,
  });

  if (error) toast.error('Failed to fetch courses.');

  // Enrich courses with review stats
  const courses = (data?.courses || []).map((course: Course) => {
    const stats = reviewStatsMap?.get(course.id || course._id);
    return {
      ...course,
      rating: stats?.avg_rating ?? course.rating ?? 0,
      review_count: stats?.total_reviews ?? course.review_count ?? 0,
    };
  });
  const pagination = data?.pagination;
  const totalCourses = data?.total || 0;
  const publishedCourses = data?.publishedCourseCount || 0;
  const draftCourses = data?.draftCourseCount || 0;
  const totalPages = pagination?.totalPages || 1;
  const allowedFormats = getAllowedFormats(filters.courseType);
  const contentFormatOptions: SelectOption[] = [
    { value: '', label: 'All Formats' },
    ...allowedFormats.map(format => ({ value: format, label: CONTENT_FORMAT_LABELS[format] })),
  ];
  const levelOptions: SelectOption[] = [
    { value: '', label: 'All Levels' },
    ...['beginner', 'intermediate', 'advanced'].map(l => ({
      value: l,
      label: l.charAt(0).toUpperCase() + l.slice(1),
    })),
  ];
  // Draft and Published are the only stages a course can be in — publishing is
  // one-way, so there is no Unpublished option to filter by.
  const stageOptions: SelectOption[] = [
    { value: '', label: 'All Stages' },
    { value: 'published', label: '🟢 Published' },
    { value: 'draft', label: '🟡 Draft' }
  ];
  const applyFilter = (name: string, value: string) => {
    setPage(1);
    setFilters(prev => {
      const next = { ...prev, [name]: value };
      // Switching course type drops a format that type can't have, so the two
      // dropdowns can never combine into an empty result set.
      if (name === 'courseType' && !getAllowedFormats(value).includes(next.contentFormat)) {
        next.contentFormat = '';
      }
      return next;
    });
  };

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    applyFilter(e.target.name, e.target.value);
  };

  // SimpleSelect reports the '' ("All …") option as null, so coerce it back.
  const handleSelectChange = (name: string) => (value: string | number | null) =>
    applyFilter(name, value == null ? '' : String(value));

  // Handle rating click - navigate to course preview with reviews tab
  const handleRatingClick = (courseId: string) => {
    navigate(`/admin/course/preview/${courseId}?tab=reviews`);
  };

  const sortOptions = [
    { value: 'updatedAt-desc', label: 'Recently Updated' },
    { value: 'createdAt-desc', label: 'Newest' },
    { value: 'users_assigned-desc', label: 'Most Popular' },
    { value: 'rating-desc', label: 'Highest Rated' },
    { value: 'title-asc', label: 'Title (A-Z)' },
    { value: 'title-desc', label: 'Title (Z-A)' },
  ];

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-brand-blue-600 to-brand-purple-600 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
              <BookOpen className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Course Catalog</h1>
              <p className="text-brand-blue-100">Discover and manage all courses in the platform.</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {canWrite && (
              <Link
                to="/admin/course/create-manual"
                className="flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-lg hover:bg-white/30 transition-colors"
                title="Create a course manually and build the modules yourself"
              >
                <Edit className="w-5 h-5" />
                <span className="font-medium">Create Manually</span>
              </Link>
            )}
            {canGenerate ? (
              <Link
                to="/admin/course/add-course"
                className="flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-lg hover:bg-white/30 transition-colors"
              >
                <Sparkles className="w-5 h-5" />
                <span className="font-medium">Generate Course</span>
              </Link>
            ) : (
              <span
                className="flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-lg opacity-50 cursor-not-allowed"
                title="You do not have permission to generate courses"
              >
                <Sparkles className="w-5 h-5" />
                <span className="font-medium">Generate Course</span>
              </span>
            )}
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
            <div className="text-2xl font-bold">{totalCourses}</div>
            <div className="text-sm text-brand-blue-200">Total Courses</div>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
            <div className="text-2xl font-bold">{publishedCourses}</div>
            <div className="text-sm text-brand-blue-200">Published Courses</div>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
            <div className="text-2xl font-bold">{draftCourses}</div>
            <div className="text-sm text-brand-blue-200">Draft Courses</div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
              <input
                type="text"
                name="search"
                value={filters.search}
                onChange={handleFilterChange}
                placeholder="Search courses..."
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => setShowFilters(!showFilters)} className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
              <SlidersHorizontal className="w-4 h-4" />
              Filters
              <ChevronDown className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
            </button>
            <div className="flex items-center gap-1 border border-gray-300 rounded-lg p-1">
              <button onClick={() => setViewMode('grid')} className={`p-2 rounded ${viewMode === 'grid' ? 'bg-primary-100 text-primary-600' : 'text-gray-600'}`}>
                <Grid className="w-4 h-4" />
              </button>
              <button onClick={() => setViewMode('list')} className={`p-2 rounded ${viewMode === 'list' ? 'bg-primary-100 text-primary-600' : 'text-gray-600'}`}>
                <List className="w-4 h-4" />
              </button>
            </div>
            {/* Fixed width: SimpleSelect is w-full, which would stretch in this
                flex toolbar. */}
            <div className="w-52">
              <SimpleSelect
                value={`${filters.sortBy}-${filters.sortOrder}`}
                onChange={(value) => {
                  if (value == null) return;
                  const [sortBy, sortOrder] = String(value).split('-') as [typeof filters.sortBy, typeof filters.sortOrder];
                  setFilters(prev => ({ ...prev, sortBy, sortOrder }));
                }}
                options={sortOptions}
                isSearchable={false}
              />
            </div>
          </div>
        </div>
        {showFilters && (
          <div className={`grid grid-cols-1 md:grid-cols-2 ${allowedFormats.length > 0 ? 'lg:grid-cols-5' : 'lg:grid-cols-4'} gap-4 p-4 bg-gray-50 rounded-lg mb-6`}>
            <SimpleSelect
              label="Course Type"
              value={filters.courseType}
              onChange={handleSelectChange('courseType')}
              options={COURSE_TYPE_OPTIONS}
              isSearchable={false}
            />
            {/* SCORM packages have no delivery type, so the format filter is
                irrelevant for them and drops out of the grid entirely. */}
            {allowedFormats.length > 0 && (
              <SimpleSelect
                label="Content Format"
                value={filters.contentFormat}
                onChange={handleSelectChange('contentFormat')}
                options={contentFormatOptions}
                isSearchable={false}
              />
            )}
            <SimpleSelect
              label="Level"
              value={filters.difficulty}
              onChange={handleSelectChange('difficulty')}
              options={levelOptions}
              isSearchable={false}
            />
            <SimpleSelect
              label="Stage"
              value={filters.stage}
              onChange={handleSelectChange('stage')}
              options={stageOptions}
              isSearchable={false}
            />
            <div className="flex items-end">
              <button
                onClick={() => {
                  setFilters({
                    search: '',
                    difficulty: '',
                    stage: '',
                    courseType: '',
                    contentFormat: '',
                    sortBy: 'updatedAt',
                    sortOrder: 'desc'
                  });
                  setPage(1);
                }}
                className="w-full btn btn-secondary"
              >
                Clear Filters
              </button>
            </div>
          </div>
        )}
      </div>

      {isLoadingCourses ? (
        <div className={`${viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6' : 'space-y-6'}`}>
          {Array.from({ length: 9 }).map((_, index) => (
            <CardSkeleton key={index} showImage={true} lines={3} />
          ))}
        </div>
      ) : courses.length === 0 ? (
        <div className="text-center py-12 card">
          <BookOpen className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No courses found</h3>
          <p className="text-gray-600 mb-4">Try adjusting your filters{canWrite ? ' or create a new course' : ''}.</p>
          {canWrite && (
            <Link to="/admin/course/add-course" className="btn btn-primary">
              Create Course
            </Link>
          )}
        </div>
      ) : (
        <>
          <div className={`${viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6' : 'space-y-6'}`}>
            {courses.map((course: Course) => (
              <CourseCard
                key={course._id}
                course={course}
                isListView={viewMode === 'list'}
                onRatingClick={handleRatingClick}
                canWrite={canWrite}
              />
            ))}
          </div>
          {totalPages > 1 && (
            <div className="mt-6">
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                totalItems={totalCourses}
                itemsPerPage={limit}
                onPageChange={setPage}
                onItemsPerPageChange={(newLimit) => {
                  setLimit(newLimit);
                  setPage(1);
                }}
                itemsPerPageOptions={[9, 12, 21, 48]}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}